import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { PopupMenu, type PopupMenuEntry } from '@kerfjs/ui/popup-menu';
import type { SafeHtml } from 'kerfjs';
import { Clipboard, Sparkles, Trash2 } from 'lucide';

import { analysisReport, DECISION_ACTIONS, decisionsDemandId } from '../data/state.js';
import { menuProps, type MenuTrigger } from '../lib/action-menu.js';
import { hasApplicableRecommendation } from '../lib/batch-decision.js';
import { decisionSubmenuEntry } from './decision-submenu.js';

/** The canonical actions for one line item. Row kebabs and one-item selection
 * menus deliberately render this same component so their labels and order
 * cannot drift apart (HS2-E52Y1K). */
export function LineItemActions({
  demandId,
  lineId,
  currentAction,
  canDecide = true,
  canRemove = true,
  trigger,
  lazy = false,
}: {
  demandId?: string;
  lineId: string;
  currentAction?: string | null;
  canDecide?: boolean;
  /** False on a decided demand whose lines are locked but whose decisions are not (HS2-Y50H8E). */
  canRemove?: boolean;
  trigger: MenuTrigger;
  /** Render only the trigger, with no entries, until the row's menu is first opened (HS2-D2G57R). */
  lazy?: boolean;
}): SafeHtml {
  if (lazy) return <PopupMenu {...menuProps(trigger, [])} />;
  // An untouched line can take the displayed pricing check's recommendation
  // directly; bulk-apply cites that version as the decision source (HS2-A0851K).
  // Only once this demand's decisions have loaded, so an already-decided line
  // never flashes the item (and reflows the open menu) while they are in flight.
  const canAccept =
    canDecide &&
    demandId !== undefined &&
    decisionsDemandId.value === demandId &&
    (currentAction === null || currentAction === undefined) &&
    hasApplicableRecommendation(lineId, analysisReport.value);
  const entries: PopupMenuEntry[] = [];
  if (canAccept) {
    entries.push({
      label: 'Accept AI Recommendation',
      action: 'line-accept-recommendation',
      icon: <LucideIcon icon={Sparkles} name="" />,
      attributes: { 'data-item-id': lineId },
    });
  }
  if (canDecide) {
    entries.push(
      decisionSubmenuEntry({
        actions: DECISION_ACTIONS,
        actionName: 'line-decide',
        lineId,
        currentAction,
        clarifyAction: 'line-clarify-open',
      }),
    );
  }
  if (demandId !== undefined) {
    entries.push({
      label: 'Copy Item Reference',
      action: 'line-copy-reference',
      icon: <LucideIcon icon={Clipboard} name="" />,
      attributes: { 'data-item-id': lineId, 'data-reference-demand-id': demandId },
    });
  }
  if (canRemove)
    entries.push(
      { kind: 'divider' },
      {
        label: 'Remove',
        action: 'line-remove',
        tone: 'danger',
        icon: <LucideIcon icon={Trash2} name="" />,
        attributes: { 'data-item-id': lineId },
      },
    );
  return <PopupMenu {...menuProps(trigger, entries)} />;
}
